import useAuthenticatedAdmin from '../hooks/useAuthenticatedAdmin';
import PermissionRoute from '../components/PermissionRoute';
import React, { useState } from "react";
import {
  Routes as RouterRoutes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Routes } from "../routes";

// Components
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Preloader from "../components/Preloader";

// import Upgrade from "./Upgrade";
import DashboardOverview from "./dashboard/DashboardOverview";
import Reports from "./Reports";
import Transactions from "./Transactions";
import Settings from "./Settings";
import Customers from "./Customers";
import { GeneralInfoForm } from "./CreateCustomer";
import { CreateCompanyForm } from "./CreateCompany";
import { EditCompanyForm } from "./EditCompany";
import CompanySettingsPage from "./CompanySettings";
import { CreateBranchForm } from "./CreateBranch";
import CreateAdminForm from "./CreateAdmin";
import { CreateRoleForm } from "./CreateRole";
import { EditBranchForm } from "./EditBranch";
import { CustomerEditForm } from "./EditCustomer";
import { EditAdminForm } from "./EditAdmin";
import { EditRoleForm } from "./EditRole";
import { CreatePrincipalForm } from "./CreatePrincipal";
import { EditPrincipalForm } from "./EditPrincipal";
import { CreateQuoteForm } from "./CreateQuote";
import { EditQuoteForm } from "./EditQuotes";
import ConvertPoSalesPage from "./ConvertPoSales";
import PoSales from "./PoSales";
import PaymentTerms from "./PaymentTerms";
import { CreatePaymentTerm } from "./CreatePaymentTerm";
import { EditPaymentTerm } from "./EditPaymentTerm";
import { CreateProductForm } from "./CreateProductCategory";
import { EditProductForm } from "./EditProductCategory";
import { CreateIndustryForm } from "./CreateIndustries";
import { EditIndustryForm } from "./EditIndustries";
import { CreateSubIndustryForm } from "./CreateSubIndustries";
import { EditSubIndustryForm } from "./EditSubIndustries";
import { CreateDepartmentForm } from "./CreateDepartment";
import { EditDepartmentForm } from "./EditDepartment";
import Branches from "./Branches";
import SoftBranch from "./SoftBranch";
import ProductCategories from "./ProductCategories";
import Company from "./Company";
import SoftCompany from "./SoftCompany";
import Quotes from "./Quotes";
import Admin from "./Admin";
import Role from "./Role";
import Principal from './Principal';
import SoftPrincipal from "./SoftPrincipal";
import Industries from "./Industries";
import SubIndustries from "./SubIndustries";
import Departments from "./Departments";
import Signin from "./examples/Signin";
import NotFoundPage from "./examples/NotFound";
import ServerError from "./examples/ServerError";

import Privateroutes from "./PrivateRoutes";

// Route wrappers
const RouteWithLoader = ({ component: Component }) => <Component />;

const RouteWithSidebar = ({ component: Component }) => {
  const location = useLocation();
  const { admin, loading: permissionLoading } = useAuthenticatedAdmin(location.pathname, {
    includeCompanies: location.pathname !== Routes.Quotes.path,
  });
  const localStorageIsSettingsVisible = () =>
    localStorage.getItem("settingsVisible") !== "false";
  const [showSettings, setShowSettings] = useState(localStorageIsSettingsVisible);

  const toggleSettings = () => {
    setShowSettings(!showSettings);
    localStorage.setItem("settingsVisible", !showSettings);
  };

  return (
    <>
      <Preloader show={permissionLoading} />
      <Sidebar admin={admin} />
      <Navbar admin={admin} />
      <main className="content">
        <div className="app-page">
          {!permissionLoading && Component && (
            <PermissionRoute path={location.pathname} admin={admin}><Component /></PermissionRoute>
          )}
        </div>
        <Footer toggleSettings={toggleSettings} showSettings={showSettings} />
      </main>
    </>
  );
};


const AppRouter = () => {
  const isAuthenticated = !!sessionStorage.getItem('quotetoken');

  return (
    <RouterRoutes>

      <Route path={Routes.Presentation.path} element={ isAuthenticated ? ( <Navigate to={Routes.DashboardOverview.path} replace />) : ( <Navigate to={Routes.Signin.path} replace /> )}
      />
      
      {/* Auth Pages */}
      <Route path={Routes.Signin.path} element={ isAuthenticated ? ( <Navigate to={Routes.DashboardOverview.path} replace />) : ( <RouteWithLoader component={Signin} />)}/>

      <Route path={Routes.NotFound.path} element={<RouteWithLoader component={NotFoundPage} />} />
      <Route path={Routes.ServerError.path} element={<RouteWithLoader component={ServerError} />} />

      {/* Main Pages */}
      <Route element={<Privateroutes />}>
        <Route path={Routes.DashboardOverview.path} element={<RouteWithSidebar component={DashboardOverview} />} />
        <Route path={Routes.Reports.path} element={<RouteWithSidebar component={Reports} />} />
        <Route path={Routes.Transactions.path} element={<RouteWithSidebar component={Transactions} />} />
        <Route path={Routes.Settings.path} element={<RouteWithSidebar component={Settings} />} />
        <Route path={Routes.Customers.path} element={<RouteWithSidebar component={Customers} />} />
        <Route path={Routes.CreateCustomers.path} element={<RouteWithSidebar component={GeneralInfoForm} />} />
        <Route path={Routes.EditCustomers.path} element={<RouteWithSidebar component={CustomerEditForm} />} />
        <Route path={Routes.Branches.path} element={<RouteWithSidebar component={Branches} />} />
        <Route path={Routes.SoftBranches.path} element={<RouteWithSidebar component={SoftBranch} />} />
        <Route path={Routes.CreateBranch.path} element={<RouteWithSidebar component={CreateBranchForm} />} />
        <Route path={Routes.EditBranch.path} element={<RouteWithSidebar component={EditBranchForm} />} />
        <Route path={Routes.Product.path} element={<RouteWithSidebar component={ProductCategories} />} />
        <Route path={Routes.CreateProduct.path} element={<RouteWithSidebar component={CreateProductForm} />} />
        <Route path={Routes.EditProduct.path} element={<RouteWithSidebar component={EditProductForm} />} />
        <Route path={Routes.Company.path} element={<RouteWithSidebar component={Company} />} />
        <Route path={Routes.CompanySettings.path} element={<RouteWithSidebar component={CompanySettingsPage} />} />
        <Route path={Routes.CompanySettingsListing.path} element={<RouteWithSidebar component={CompanySettingsPage} />} />
        <Route path={Routes.SoftCompany.path} element={<RouteWithSidebar component={SoftCompany} />} />
        <Route path={Routes.CreateCompany.path} element={<RouteWithSidebar component={CreateCompanyForm} />} />
        <Route path={Routes.EditCompany.path} element={<RouteWithSidebar component={EditCompanyForm} />} />
        <Route path={Routes.Quotes.path} element={<RouteWithSidebar component={Quotes} />} />
        <Route path={Routes.CreateQuotes.path} element={<RouteWithSidebar component={CreateQuoteForm} />} />
        <Route path={Routes.EditQuotes.path} element={<RouteWithSidebar component={EditQuoteForm} />} />
        <Route path={Routes.ConvertPoSales.path} element={<RouteWithSidebar component={ConvertPoSalesPage} />} />
        <Route path={Routes.PoSales.path} element={<RouteWithSidebar component={PoSales} />} />
        <Route path={Routes.PaymentTerms.path} element={<RouteWithSidebar component={PaymentTerms} />} />
        <Route path={Routes.CreatePaymentTerm.path} element={<RouteWithSidebar component={CreatePaymentTerm} />} />
        <Route path={Routes.EditPaymentTerm.path} element={<RouteWithSidebar component={EditPaymentTerm} />} />
        <Route path="/vendor-permissions" element={<Navigate to={Routes.Admin.path} replace />} />
        <Route path={Routes.Admin.path} element={<RouteWithSidebar component={Admin} />} />
        <Route path={Routes.CreateAdmin.path} element={<RouteWithSidebar component={CreateAdminForm} />} />
        <Route path={Routes.EditAdmin.path} element={<RouteWithSidebar component={EditAdminForm} />} />
        <Route path={Routes.Roles.path} element={<RouteWithSidebar component={Role} />} />
        <Route path={Routes.CreateRole.path} element={<RouteWithSidebar component={CreateRoleForm} />} />
        <Route path={Routes.EditRole.path} element={<RouteWithSidebar component={EditRoleForm} />} />
        <Route path={Routes.Principals.path} element={<RouteWithSidebar component={Principal} />} />
        <Route path={Routes.SoftPrincipals.path} element={<RouteWithSidebar component={SoftPrincipal} />} />
        <Route path={Routes.CreatePrincipal.path} element={<RouteWithSidebar component={CreatePrincipalForm} />} />
        <Route path={Routes.EditPrincipal.path} element={<RouteWithSidebar component={EditPrincipalForm} />} />
        <Route path={Routes.Industries.path} element={<RouteWithSidebar component={Industries} />} />
        <Route path={Routes.CreateIndustries.path} element={<RouteWithSidebar component={CreateIndustryForm} />} />
        <Route path={Routes.EditIndustries.path} element={<RouteWithSidebar component={EditIndustryForm} />} />
        <Route path={Routes.SubIndustries.path} element={<RouteWithSidebar component={SubIndustries} />} />
        <Route path={Routes.CreateSubIndustries.path} element={<RouteWithSidebar component={CreateSubIndustryForm} />} />
        <Route path={Routes.EditSubIndustries.path} element={<RouteWithSidebar component={EditSubIndustryForm} />} />
        <Route path={Routes.Departments.path} element={<RouteWithSidebar component={Departments} />} />
        <Route path={Routes.CreateDepartments.path} element={<RouteWithSidebar component={CreateDepartmentForm} />} />
        <Route path={Routes.EditDepartments.path} element={<RouteWithSidebar component={EditDepartmentForm} />} />
      </Route>

      {/* Fallback Route */}
      <Route path="*" element={<Navigate to={Routes.NotFound.path} />} />
    </RouterRoutes>
  );
};

export default AppRouter;

